import { Typography, useTheme, useMediaQuery, Paper, Box } from '@mui/material';
import {
  CcfAvailableIcon,
  CcfTypography,
  CcfWorkingIcon,
  CcfUnavailableIcon,
  CcfBackIcon,
  CcfTable,
  useTranslator,
  RowDataStructure,
  CcfTooltip,
  CcfNoAssignmentIcon,
} from '@nice-ccf/ui-controls';
import skillDetailsStyles from './ccf-skill-details.styles';
import { useSelector } from 'react-redux';
import { CHANNEL_ICON_NAME, iconList } from '../ccf-icon/ccf-icon-list';
import { useEffect, useState } from 'react';
import { convertMinutestoHours } from '../../util/uiValidationUtils';
import { MediaTypeId } from '@nice-ccf/core-sdk';
import { CcfTranslationKey } from '@nice-ccf/i18n';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface CcfSkillDetailsProps {
  isAppSpace?: boolean;
  rowItemClicked?: number;
  setSkillDetailsScreen?: React.Dispatch<React.SetStateAction<boolean>>;
}

/**-
 * component for CcfSkillDetails
 * @returns component for rendering the Skill Details
 * @example <CcfSkillDetails />
 */
const CcfSkillDetails = ({
  setSkillDetailsScreen,
  isAppSpace,
  rowItemClicked,
}: CcfSkillDetailsProps) => {
  const theme = useTheme();
  const isMdView = useMediaQuery(
    theme.breakpoints.between(
      theme.breakpoints.values.md,
      theme.breakpoints.values.lg
    )
  );
  const isSmView = useMediaQuery(
    theme.breakpoints.between(
      theme.breakpoints.values.sm,
      theme.breakpoints.values.md
    )
  );
  const [translate, locale] = useTranslator();
  const styles = skillDetailsStyles(theme, isMdView, isAppSpace);
  const inboundSkills: any = [
    {
      skillId: 638920,
      skillName: 'sales',
      queueCount: 1,
      subSkillsName: [
        { contactIdentifier: '40056754', count: 1, waitTime: 4566554 },
      ],
      longestQueueTimeInSeconds: 4566554,
      agentsAvailable: 15,
      agentsUnavailable: 10,
      agentsWorking: 15,
      mediaType: 4,
    },
    {
      skillId: 745094,
      skillName: 'customerSupport',
      queueCount: 1,
      subSkillsName: [
        { contactIdentifier: '40052344', count: 1, waitTime: 4579554 },
      ],
      longestQueueTimeInSeconds: 4579554,
      agentsAvailable: 15,
      agentsUnavailable: 10,
      agentsWorking: 15,
      mediaType: 4,
    },
    {
      skillId: 745078,
      skillName: 'followUp',
      queueCount: 1,
      subSkillsName: [
        { contactIdentifier: '40014754', count: 1, waitTime: 4567534 },
      ],
      longestQueueTimeInSeconds: 4567534,
      agentsAvailable: 15,
      agentsUnavailable: 10,
      agentsWorking: 15,
      mediaType: 4,
    },
    {
      skillId: 745097,
      skillName: 'VoiceMail_1',
      queueCount: 1,
      subSkillsName: [
        { contactIdentifier: '400167534', count: 1, waitTime: 4167534 },
      ],
      longestQueueTimeInSeconds: 4167534,
      agentsAvailable: 12,
      agentsUnavailable: 10,
      agentsWorking: 12,
      mediaType: 5,
    },
    {
      skillId: 745345,
      skillName: 'VoiceMail_2',
      queueCount: 1,
      subSkillsName: [
        { contactIdentifier: '400127534', count: 1, waitTime: 4127534 },
      ],
      longestQueueTimeInSeconds: 4127534,
      agentsAvailable: 12,
      agentsUnavailable: 10,
      agentsWorking: 12,
      mediaType: 5,
    },
    {
      skillId: 795093,
      skillName: 'sales',
      queueCount: 2,
      subSkillsName: [
        { contactIdentifier: 'Instagram', count: 1, waitTime: 34546 },
        { contactIdentifier: 'Chat', count: 1, waitTime: 31546 },
      ],
      longestQueueTimeInSeconds: 66092,
      agentsAvailable: 12,
      agentsUnavailable: 10,
      agentsWorking: 12,
      mediaType: 9,
    },
    {
      skillId: 795099,
      skillName: 'customerSupport',
      queueCount: 3,
      subSkillsName: [
        { contactIdentifier: 'Email', count: 1, waitTime: 34346 },
        { contactIdentifier: 'Chat', count: 2, waitTime: 61546 },
      ],
      longestQueueTimeInSeconds: 95892,
      agentsAvailable: 12,
      agentsUnavailable: 10,
      agentsWorking: 12,
      mediaType: 9,
    },
    {
      skillId: 795091,
      skillName: 'followUp',
      queueCount: 2,
      subSkillsName: [
        { contactIdentifier: 'Instagram', count: 1, waitTime: 35746 },
        { contactIdentifier: 'Facebook', count: 1, waitTime: 35546 },
      ],
      longestQueueTimeInSeconds: 71292,
      agentsAvailable: 12,
      agentsUnavailable: 10,
      agentsWorking: 12,
      mediaType: 9,
    },
    {
      skillId: 795092,
      skillName: 'escalations',
      queueCount: 2,
      subSkillsName: [
        { contactIdentifier: 'Facebook', count: 1, waitTime: 34226 },
        { contactIdentifier: 'Linkedin', count: 1, waitTime: 39546 },
      ],
      longestQueueTimeInSeconds: 73772,
      agentsAvailable: 12,
      agentsUnavailable: 10,
      agentsWorking: 12,
      mediaType: 9,
    },
  ];
  const queueSkillDetails = {
    longestPhoneWait: 0,
    longestWorkItemWait: 0,
    longestVoiceMailWait: 0,
    longestDigitalWait: 0,
    totalPhoneContacts: 3,
    totalWorkItemContacts: 0,
    totalVoiceMailContacts: 2,
    totalDigitalContacts: 9,
    totalAllContacts: 0,
  };
  const [showExpandedRow, setShowExpandedRow] = useState(false);
  const [rowIDForExpansion, setRowIDForExpansion] = useState(0);
  const [totalContacts, setTotalContacts] = useState(0);
  const [backButtonText, setBackButtonText] = useState('inboundVoice');
  const [skillIcon, setSkillIcon] = useState('ib-call');
  const tableHeadersXlView = [
    translate('skillname'),
    translate('inQueue'),
    translate('longestWait'),
    translate('agentStates'),
  ];
  const tableHeadersMdView = [
    translate('skillname'),
    translate('inQueue'),
    translate('wait'),
  ];
  const tableHeadersSmView = [translate('skillname'), translate('inQueue')];
  const colWidthsXlView = ['30%', '15%', '25%', '30%'];
  const colWidthsMdView = ['50%', '20%', '30%'];
  const colWidthsSmView = ['70%', '30%'];
  const rowData: RowDataStructure[] = [];
  const filteredSkill = inboundSkills.filter((skill: any) => {
    return skill.mediaType === rowItemClicked;
  });
  const totalSkills = filteredSkill.length;

  if (totalSkills > 0) {
    filteredSkill.forEach((skill: any, index: number) => {
      const dataStructured = [
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: 'auto',
          }}
        >
          <Box style={{ display: 'flex', alignItems: 'center' }}>
            {iconList[skillIcon]('20px', {
              htmlColor: theme.palette.text.noteLabel,
            })}
            <CcfTypography
              variant="h4"
              sx={styles.iconHeaderClasses}
              title={skill.skillName}
              aria-label={skill.skillName}
            >
              {translate(skill.skillName)}
            </CcfTypography>
          </Box>
          {showExpandedRow && rowIDForExpansion === index && (
            <>
              {skill.subSkillsName.map((subSkill: any) => (
                <Box style={{ marginLeft: '20px' }}>
                  <CcfTypography
                    variant="h4"
                    sx={styles.iconHeaderClasses}
                    title={skill.skillName}
                    aria-label={skill.skillName}
                  >
                    {subSkill?.contactIdentifier}
                  </CcfTypography>
                </Box>
              ))}
            </>
          )}
        </Box>,
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: 'auto',
          }}
        >
          <CcfTypography
            variant="h4"
            sx={styles.tableLabels}
            title={skill.queueCount.toString()}
            aria-label={skill.queueCount.toString()}
          >
            {skill.queueCount}
          </CcfTypography>
          {showExpandedRow && rowIDForExpansion === index && (
            <>
              {skill.subSkillsName.map((subSkill: any) => (
                <Box>
                  <CcfTypography
                    variant="h4"
                    sx={styles.subTableLabels}
                    title={skill.queueCount.toString()}
                    aria-label={skill.queueCount.toString()}
                  >
                    {subSkill?.count}
                  </CcfTypography>
                </Box>
              ))}
            </>
          )}
        </Box>,
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: 'auto',
          }}
        >
          <CcfTypography
            variant="h4"
            sx={styles.tableLabels}
            title={convertMinutestoHours(
              skill.longestQueueTimeInSeconds
            ).toString()}
            aria-label={convertMinutestoHours(
              skill.longestQueueTimeInSeconds
            ).toString()}
          >
            {convertMinutestoHours(skill.longestQueueTimeInSeconds)}
          </CcfTypography>
          {showExpandedRow && rowIDForExpansion === index && (
            <>
              {skill.subSkillsName.map((subSkill: any) => (
                <Box>
                  <CcfTypography
                    variant="h4"
                    sx={styles.subTableLabels}
                    title={convertMinutestoHours(
                      skill.longestQueueTimeInSeconds
                    ).toString()}
                    aria-label={convertMinutestoHours(
                      skill.longestQueueTimeInSeconds
                    ).toString()}
                  >
                    {convertMinutestoHours(subSkill?.waitTime)}
                  </CcfTypography>
                </Box>
              ))}
            </>
          )}
        </Box>,
        <Box
          sx={styles.tableRowContainer}
          title={`${translate('working')} ${translate('agents')}: ${
            skill.agentsWorking
          }`}
        >
          <Box
            sx={styles.tableRowContainer}
            title={`${translate('available')} ${translate('agents')}: ${
              skill.agentsAvailable
            }`}
          >
            <CcfAvailableIcon aria-label={translate('available')} />
            <CcfTypography
              variant="h4"
              sx={styles.agentStatesLabel}
              aria-label={skill.agentsAvailable.toString()}
            >
              {skill.agentsAvailable}
            </CcfTypography>
          </Box>
          <Box sx={styles.tableRowContainer}>
            <CcfWorkingIcon aria-label={translate('working')} />
            <CcfTypography
              variant="h4"
              sx={styles.agentStatesLabel}
              aria-label={skill.agentsWorking.toString()}
            >
              {skill.agentsWorking}
            </CcfTypography>
          </Box>
          <Box
            sx={styles.tableRowContainer}
            title={`${translate('unavailable')} ${translate('agents')}: ${
              skill.agentsUnavailable
            }`}
          >
            <CcfUnavailableIcon aria-label={translate('unavailable')} />
            <CcfTypography
              variant="h4"
              sx={styles.agentStatesLabel}
              aria-label={skill.agentsUnavailable.toString()}
            >
              {skill.agentsUnavailable}
            </CcfTypography>
          </Box>
          <Box
            sx={styles.tableRowContainer}
            style={{
              marginLeft: 'auto',
              rotate:
                showExpandedRow && rowIDForExpansion === index ? '180deg' : '',
            }}
            onClick={() => expandRow(index)}
          >
            <ExpandMoreIcon aria-label={translate('unavailable')} />
          </Box>
        </Box>,
      ];
      const object: any = { data: dataStructured };
      rowData.push(object);
    });
  }

  /**
   * Used to navigate between channel and skill details screen
   * @example
   * const onClick = back icon
   */
  const expandRow = (rowID: number) => {
    setShowExpandedRow(!showExpandedRow);
    setRowIDForExpansion(rowID);
  };

  /**
   * Used to navigate between channel and skill details screen
   * @example
   * const onClick = back icon
   */
  const handleBack = () => {
    setSkillDetailsScreen && setSkillDetailsScreen(false);
  };

  /**
   * Used to navigate between channel and skill details screen
   * @example
   * const onClick = back icon
   */
  const getTableHeaders = () => {
    if (isMdView) {
      return tableHeadersMdView;
    } else if (isSmView) {
      return tableHeadersSmView;
    } else {
      return tableHeadersXlView;
    }
  };

  /**
   * Used to get column widths of table
   * @example
   * const getColWidths()
   */
  const getColWidths = () => {
    if (isMdView) {
      return colWidthsMdView;
    } else if (isSmView) {
      return colWidthsSmView;
    } else {
      return colWidthsXlView;
    }
  };

  /**
   * Used to get rows data of table
   * @example
   * const getRowData()
   */
  const getRowData = () => {
    if (isMdView) {
      return rowData.filter((rowDetails: RowDataStructure) =>
        rowDetails.data.splice(3, 1)
      );
    } else if (isSmView) {
      return rowData.filter((rowDetails: RowDataStructure) =>
        rowDetails.data.splice(2, 2)
      );
    } else {
      return rowData;
    }
  };
  useEffect(() => {
    if (rowItemClicked === MediaTypeId.PhoneCall) {
      setTotalContacts(queueSkillDetails.totalPhoneContacts);
      setBackButtonText('inboundVoice');
      setSkillIcon('voice');
    } else if (rowItemClicked === MediaTypeId.VoiceEmail) {
      setTotalContacts(queueSkillDetails.totalVoiceMailContacts);
      setBackButtonText('voicemail');
      setSkillIcon('voice-mail');
    } else if (rowItemClicked === MediaTypeId.WorkItem) {
      setTotalContacts(queueSkillDetails.totalWorkItemContacts);
      setBackButtonText('workItem');
      setSkillIcon('work-item');
    } else if (rowItemClicked === MediaTypeId.Digital) {
      setTotalContacts(queueSkillDetails.totalDigitalContacts);
      setBackButtonText('digital');
      setSkillIcon('digital');
    }
  }, [queueSkillDetails]);

  return (
    <Box sx={styles.mainContainer}>
      <Box sx={styles.skillsContainer}>
        <Box sx={styles.skillHeader}>
          <CcfBackIcon
            data-testid="skill-details-back-icon"
            sx={styles.backIcon}
            onClick={handleBack}
            tabIndex={0}
            onKeyUp={(e) => {
              if (e.key === 'Enter') handleBack();
            }}
          />
          <CcfTypography sx={styles.skillHeading}>
            {translate(backButtonText as CcfTranslationKey)}
          </CcfTypography>
        </Box>
        <Box sx={styles.skillsInformation}>
          <Paper sx={styles.skillInfoContainer}>
            <CcfTypography
              sx={styles.skillInfoKey}
              translationKey="skills"
              aria-label={translate('skills')}
            ></CcfTypography>
            <CcfTypography
              sx={styles.skillInfoValue}
              aria-label={totalSkills.toString()}
            >
              {totalSkills}
            </CcfTypography>
          </Paper>
          <Paper sx={styles.skillInfoContainer}>
            <CcfTypography
              sx={styles.skillInfoKey}
              translationKey="contacts"
              aria-label={translate('contacts')}
            ></CcfTypography>
            <CcfTypography
              sx={styles.skillInfoValue}
              aria-label={totalContacts.toString()}
            >
              {totalContacts}
            </CcfTypography>
          </Paper>
        </Box>
      </Box>
      <Box
        sx={totalSkills > 0 ? styles.skillItems : styles.skillNoItems}
        data-testid="inbound-skill-list"
      >
        <CcfTable
          colWidths={getColWidths()}
          headersData={getTableHeaders()}
          rowsData={getRowData()}
          tableStyle={styles.tableStyle}
          rowStyle={styles.tableBodyRow}
          headerStyle={styles.tableHeadRow}
          containerStyle={styles.customTableContainer}
          isHideSortIcon={true}
        ></CcfTable>
      </Box>
      {totalSkills === 0 && (
        <Box sx={styles.noSkillsInformation}>
          <CcfNoAssignmentIcon style={{ height: '50', width: '50' }} />
          <CcfTypography
            sx={styles.noSkillInfoKey}
            translationKey="noSkillsAssigned"
          ></CcfTypography>
        </Box>
      )}
    </Box>
  );
};

export default CcfSkillDetails;
